import type { ModuleId, WidgetDefinition, WidgetInstance, WidgetParams, WidgetSize } from './types';
import { WIDGET_SIZE_WEIGHT } from './types';

const registry = new Map<string, WidgetDefinition>();

export const registerWidgets = (definitions: WidgetDefinition[]): void => {
  definitions.forEach((definition) => registry.set(definition.id, definition));
};

export const getWidget = (id: string): WidgetDefinition | undefined => registry.get(id);

export const allWidgets = (): WidgetDefinition[] => [...registry.values()];

export const modulesWithWidgets = (): ModuleId[] => {
  const seen = new Set<ModuleId>();
  allWidgets().forEach((definition) => seen.add(definition.module));
  return [...seen];
};

export const defaultParams = (definition: WidgetDefinition): WidgetParams => {
  const params: WidgetParams = {};
  definition.params?.forEach((param) => { params[param.key] = param.defaultValue; });
  return params;
};

export const resolveParams = (definition: WidgetDefinition, instance: WidgetInstance): WidgetParams => ({
  ...defaultParams(definition),
  ...(instance.params ?? {}),
});

export const resolveSize = (definition: WidgetDefinition, size: WidgetSize): WidgetSize => {
  if (definition.sizes.includes(size)) return size;
  const wanted = WIDGET_SIZE_WEIGHT[size];
  return [...definition.sizes].sort(
    (a, b) => Math.abs(WIDGET_SIZE_WEIGHT[a] - wanted) - Math.abs(WIDGET_SIZE_WEIGHT[b] - wanted),
  )[0] ?? definition.defaultSize;
};

let instanceCounter = 0;

export const newInstanceId = (widgetId: string): string => {
  instanceCounter += 1;
  return `${widgetId}~${Date.now().toString(36)}${instanceCounter.toString(36)}`;
};

export type WidgetSetup = Pick<WidgetInstance, 'params' | 'pins'>;

export const instantiate = (
  definition: WidgetDefinition,
  size?: WidgetSize,
  { params, pins }: WidgetSetup = {},
): WidgetInstance => ({
  instanceId: newInstanceId(definition.id),
  widgetId: definition.id,
  size: size ?? definition.defaultSize,
  ...(params && Object.keys(params).length > 0 ? { params } : {}),
  ...(pins ? { pins } : {}),
});
